import React, { useCallback, useContext, useEffect, useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoWarning } from "react-icons/io5";
import { LuArrowLeft, LuCalendarDays, LuMapPin, LuTicket } from "react-icons/lu";
import { Link, useParams } from "react-router-dom";
import ClientAuthContext from "../../context/ClientAuthContext";
import { serverInstance } from "../../service/axios";
import { formatAmount, formatPricingDate, getPricingRows, getTierLabel } from "../meetings/meetingPricing";
import "./event.scss";

const getAuthHeaders = (token) => ({
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  "Content-Type": "application/json",
});

class EventModel {
  id
  title
  description
  dt
  location
  fileUrl
  registration_fee
}

const Event = ({ data, language }) => {
  const params = useParams();
  const { userData } = useContext(ClientAuthContext);
  const [event, setEvent] = useState(new EventModel());
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [capacityFull, setCapacityFull] = useState(false);
  const [registered, setRegistered] = useState(false);
  const pricingRows = getPricingRows(event, { allowDiscountOnly: true });
  const hasRegistration = pricingRows.length > 0;
  const hasCapacityLimit = event?.limit_capacity === true
    && event?.capacity !== undefined
    && event?.capacity !== null
    && String(event.capacity).trim() !== "";
  const showCapacityFullLabel = capacityFull && !registered && hasCapacityLimit;
  const showRegisterLink = hasRegistration && (!capacityFull || registered);
  const registerLinkText = registered
    ? (data.showMyRegistration || (language ? "SHOW MY REGISTRATION" : "МИНИЙ БҮРТГЭЛ ХАРАХ"))
    : (data.register || data.registerButton || (language ? "Register" : "Бүртгүүлэх"));
  const hasEarlyBirdPricing = pricingRows.some((row) => row.discount_only || Number(row.discount_amount) > 0);
  const earlyBirdPricingEndDates = Array.from(new Set(pricingRows
    .filter((row) => row.discount_only || (Number(row.discount_amount) > 0 && Number(row.original_amount) > Number(row.amount)))
    .map((row) => formatPricingDate(row.discount_ends_at, language))
    .filter(Boolean)));
  const formattedDate = event.dt
    ? new Date(event.dt).toLocaleString(language ? "en-US" : "mn-MN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    : "";
  const eventImageUrl = event.fileUrl || "";
  const eventLabel = language ? "Event" : "Эвент";
  const aboutLabel = language ? "About this event" : "Эвентийн тухай";
  const dateLabel = language ? "Date" : "Огноо";
  const locationLabel = language ? "Location" : "Байршил";
  const backLabel = language ? "Back to events" : "Эвентүүд рүү буцах";

  const loadRegistrationStatus = useCallback(() => {
    if (!params.id || !userData?.id) {
      setRegistered(false);
      return;
    }

    const token = localStorage.getItem("msidclienttoken");
    serverInstance
      .get(`/client/event-attendee?event_id=${encodeURIComponent(params.id)}`, {
        headers: getAuthHeaders(token),
      })
      .then((res) => {
        const attendee = res?.data?.data;
        setRegistered(!!attendee?.email);
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setRegistered(false);
        }
      });
  }, [params.id, userData?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!params.id) {
      return;
    }

    setLoading(true);
    setCapacityFull(false);

    const loadEvent = serverInstance.post("/app/item", { class: "event", id: params.id }, {
      headers: {
        "Content-Type": "application/json",
      }
    });
    const loadCapacity = serverInstance.get(`/app/event-capacity?event_id=${encodeURIComponent(params.id)}`);

    Promise.allSettled([loadEvent, loadCapacity])
      .then(([eventResult, capacityResult]) => {
        if (eventResult.status === "fulfilled") {
          const item = eventResult.value?.data?.item || {};
          setEvent({
            ...item,
            fileUrl: item.fileUrl
              ? `https://msid-asset.s3.ap-northeast-2.amazonaws.com/${item.fileUrl}`
              : "",
          });
          setErrorMsg("");
        } else {
          setEvent(new EventModel());
          setErrorMsg(eventResult.reason?.response?.data?.message || eventResult.reason?.message || String(eventResult.reason));
        }

        if (capacityResult.status === "fulfilled") {
          setCapacityFull(capacityResult.value?.data?.capacity_full === true);
        } else {
          setCapacityFull(false);
        }
      })
      .finally(() => setLoading(false));
  }, [params.id]);

  useEffect(() => {
    loadRegistrationStatus();
  }, [loadRegistrationStatus]);

  return (
    <div className="event">
      {loading ? (
        <div className="event-loading">
          <AiOutlineLoading3Quarters size={28} className="loading-spinner"/>
        </div>
      ) : (
        <main className="event-shell">
          <Link className="event-back-link" to="/events">
            <LuArrowLeft aria-hidden="true" />
            <span>{backLabel}</span>
          </Link>

          {errorMsg ? (
            <div className="error-msg">
              <IoWarning/>
              <span>{errorMsg}</span>
            </div>
          ) : null}

          {!errorMsg ? (
            <>
              <section className={"event-hero" + (eventImageUrl ? "" : " event-hero-no-image")}>
                {eventImageUrl ? (
                  <div className="event-img">
                    <img src={eventImageUrl} alt={event.title || ""} />
                  </div>
                ) : null}

                <div className="event-hero-copy">
                  <div className="event-eyebrow">{eventLabel}</div>
                  <h1 className="title">{event.title}</h1>

                  <div className="event-dets">
                    {event.location ? (
                      <div className="event-det">
                        <LuMapPin aria-hidden="true" />
                        <div>
                          <span className="event-det-label">{locationLabel}</span>
                          <span className="event-det-value">{event.location}</span>
                        </div>
                      </div>
                    ) : null}
                    {formattedDate ? (
                      <div className="event-det">
                        <LuCalendarDays aria-hidden="true" />
                        <div>
                          <span className="event-det-label">{dateLabel}</span>
                          <span className="event-det-value">{formattedDate}</span>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {showCapacityFullLabel ? (
                    <div className="event-capacity-full">
                      <div className="capacity-limit-label">
                        {data.capacityFullLabel || (language ? "Full" : "Дүүрсэн")}
                      </div>
                    </div>
                  ) : showRegisterLink ? (
                    <Link className="event-register-link" to={`/event/register?event_id=${event.id}`}>
                      <LuTicket aria-hidden="true" />
                      <span>{registerLinkText}</span>
                    </Link>
                  ) : null}
                </div>
              </section>

              <div className="event-content-grid">
                <section className="event-story">
                  <h2>{aboutLabel}</h2>
                  <div className="event-desc">{event.description}</div>
                </section>

                {hasRegistration ? (
                  <section className="event-info">
                    <div className="meeting-section meeting-fees-section">
                      <div className="meeting-section-header">
                        <div>
                          <h2>
                            {hasEarlyBirdPricing
                              ? (language ? "Early bird prices" : "Early bird үнэ")
                              : (language ? "Registration fees" : "Бүртгэлийн төлбөр")}
                          </h2>
                          {earlyBirdPricingEndDates.length > 0 ? (
                            <div className="meeting-section-note">
                              {language ? "Ends on" : "Дуусах"}: {earlyBirdPricingEndDates.join(", ")}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="meeting-pricing">
                        {pricingRows.map((row) => {
                          const hasActiveEarlyBirdDeal = row.discount_only || (
                            Number(row.discount_amount) > 0 && Number(row.original_amount) > Number(row.amount)
                          );

                          return (
                            <div className={"pricing-card" + (hasActiveEarlyBirdDeal ? " has-early-bird" : "")} key={row.tier}>
                              <div className="pricing-type">{getTierLabel(row.tier, language)}</div>
                              {hasActiveEarlyBirdDeal ? (
                                <>
                                  <div className="pricing-amount pricing-amount-early">
                                    {formatAmount(row.amount, row.currency)}
                                  </div>
                                  {Number(row.original_amount) > Number(row.amount) ? (
                                    <div className="pricing-original pricing-original-striked">
                                      {formatAmount(row.original_amount, row.currency)}
                                    </div>
                                  ) : null}
                                </>
                              ) : (
                                <div className="pricing-amount">{formatAmount(row.amount, row.currency)}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </section>
                ) : null}
              </div>
            </>
          ) : null}
        </main>
      )}
    </div>
  );
}

export default Event;
